import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { WalletIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
import type { Wallet } from '../lib/types';

// Wallet brand colors for fallback icons
const walletColors: Record<string, { bg: string; text: string }> = {
  'Binance': { bg: 'bg-yellow-400', text: 'text-yellow-900' },
  'Coinbase': { bg: 'bg-blue-500', text: 'text-white' },
  'Bybit': { bg: 'bg-orange-500', text: 'text-white' },
  'Trust Wallet': { bg: 'bg-blue-400', text: 'text-white' },
  'Cash App': { bg: 'bg-green-500', text: 'text-white' },
  'PayPal': { bg: 'bg-blue-600', text: 'text-white' },
  'Crypto.com': { bg: 'bg-cyan-500', text: 'text-white' },
};

function WalletLogo({ wallet, size = 'md' }: { wallet: Wallet; size?: 'sm' | 'md' | 'lg' }) {
  const [imgError, setImgError] = useState(false);
  const sizeClasses = {
    sm: 'w-8 h-8',
    md: 'w-12 h-12',
    lg: 'w-16 h-16',
  };
  const brandColor = walletColors[wallet.name] || { bg: 'bg-primary-400', text: 'text-dark-900' };

  if (!wallet.logo_url || imgError) {
    return (
      <div className={`${sizeClasses[size]} ${brandColor.bg} rounded-lg flex items-center justify-center`}>
        <span className={`${brandColor.text} font-bold text-lg`}>
          {wallet.name.charAt(0)}
        </span>
      </div>
    );
  }

  return (
    <img
      src={wallet.logo_url}
      alt={`${wallet.name} logo`}
      className={`${sizeClasses[size]} rounded-lg object-contain bg-white p-1`}
      onError={() => setImgError(true)}
    />
  );
}

export default function Wallets() {
  const { data, isLoading } = useQuery({
    queryKey: ['wallets'],
    queryFn: () => api.get<{ wallets: Wallet[] }>('/wallets'),
  });

  const systemWallets = data?.wallets?.filter(w => !w.user_id) || [];
  const userWallets = data?.wallets?.filter(w => w.user_id) || [];

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-white">Supported Wallets</h1>
        <p className="mt-1 text-gray-400">Choose from our pre-configured system wallets or create custom ones</p>
      </div>

      {/* Loading State */}
      {isLoading ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="card h-24 animate-pulse">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-dark-700 rounded-lg" />
                <div className="flex-1">
                  <div className="h-4 bg-dark-700 rounded w-24 mb-2" />
                  <div className="h-3 bg-dark-700 rounded w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <>
          {/* System Wallets */}
          {systemWallets.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-primary-400 rounded-full"></span>
                System Wallets
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {systemWallets.map((wallet) => (
                  <div
                    key={wallet.id}
                    className="card flex items-center gap-4 hover:border-primary-400/30 transition-colors"
                  >
                    <WalletLogo wallet={wallet} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate">{wallet.name}</h3>
                      <p className="text-sm text-gray-400">
                        {wallet.is_active ? (
                          <span className="text-green-400">Active</span>
                        ) : (
                          <span className="text-gray-500">Inactive</span>
                        )}
                      </p>
                    </div>
                    <div className="flex-shrink-0">
                      {wallet.is_active && (
                        <span className="inline-flex items-center justify-center w-6 h-6 bg-green-500/20 rounded-full">
                          <svg className="w-3 h-3 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* User Wallets */}
          {userWallets.length > 0 && (
            <div>
              <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-2 h-2 bg-blue-400 rounded-full"></span>
                Custom Wallets
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {userWallets.map((wallet) => (
                  <div
                    key={wallet.id}
                    className="card flex items-center gap-4 hover:border-blue-400/30 transition-colors"
                  >
                    <WalletLogo wallet={wallet} />
                    <div className="flex-1 min-w-0">
                      <h3 className="font-medium text-white truncate">{wallet.name}</h3>
                      <p className="text-sm text-gray-400">
                        {wallet.is_active ? (
                          <span className="text-green-400">Active</span>
                        ) : (
                          <span className="text-gray-500">Inactive</span>
                        )}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Empty State */}
          {(!data?.wallets || data.wallets.length === 0) && (
            <div className="card text-center py-12">
              <WalletIcon className="w-16 h-16 mx-auto text-gray-600" />
              <h3 className="mt-4 text-xl font-medium text-white">No wallets available</h3>
              <p className="mt-2 text-gray-400">System wallets will be available shortly</p>
            </div>
          )}
        </>
      )}

      {/* Info Card */}
      <div className="card bg-gradient-to-r from-dark-800 to-dark-900">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-primary-400/10 rounded-lg">
            <WalletIcon className="w-6 h-6 text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white mb-2">About Wallet Integration</h2>
            <p className="text-gray-400 text-sm leading-relaxed">
              FlashDoom integrates with multiple cryptocurrency wallets to send emails from your preferred platforms.
              System wallets are pre-configured and ready to use. You can also add custom wallets
              to match your specific branding needs. All wallets support high-volume email delivery
              with built-in rate limiting and throttling.
            </p>
          </div>
        </div>

        {/* Supported Brands */}
        <div className="mt-6 pt-6 border-t border-dark-700">
          <h3 className="text-sm font-medium text-gray-400 mb-4">Supported Brands</h3>
          <div className="flex flex-wrap gap-3">
            {['Binance', 'Coinbase', 'Bybit', 'Trust Wallet', 'Cash App', 'PayPal', 'Crypto.com'].map((brand) => (
              <span
                key={brand}
                className="inline-flex items-center px-3 py-1.5 bg-dark-900 rounded-full text-sm text-gray-300 border border-dark-700"
              >
                {brand}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
